/**
 * useFocusTrap — confine keyboard focus inside a container while it is active.
 *
 * Usage:
 *   const ref = useRef(null);
 *   useFocusTrap(ref, { active: open, onEscape: onClose });
 *   return open ? <div ref={ref}>modal content…</div> : null;
 *
 * Behavior:
 *   - On activate: saves the currently-focused element, focuses the first
 *     focusable descendant (or the container itself if none).
 *   - On Tab / Shift+Tab: wraps focus so it cannot leave the container.
 *   - On Escape: invokes onEscape (typically the caller's close handler).
 *   - On deactivate: returns focus to the element that had it when the
 *     trap first activated.
 *
 * Degrades cleanly when document is unavailable (SSR): does nothing.
 */

import { useEffect } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(",");

function getFocusable(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true"
  );
}

export function useFocusTrap(ref, { active, onEscape } = {}) {
  useEffect(() => {
    if (!active || !ref || !ref.current) return undefined;
    if (typeof document === "undefined") return undefined;

    const container = ref.current;
    const previouslyFocused = document.activeElement;

    // Focus the first focusable element, or the container itself.
    const focusables = getFocusable(container);
    if (focusables.length > 0) {
      focusables[0].focus();
    } else if (typeof container.focus === "function") {
      container.setAttribute("tabindex", "-1");
      container.focus();
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (typeof onEscape === "function") {
          event.preventDefault();
          onEscape();
        }
        return;
      }
      if (event.key !== "Tab") return;

      // Re-query each press — contents may have changed.
      const current = getFocusable(container);
      if (current.length === 0) {
        event.preventDefault();
        return;
      }
      const first = current[0];
      const last = current[current.length - 1];
      const activeEl = document.activeElement;

      if (event.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last || !container.contains(activeEl)) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener("keydown", onKeyDown);

    return () => {
      container.removeEventListener("keydown", onKeyDown);
      // Restore focus to the element that had it before the trap activated.
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        try { previouslyFocused.focus(); } catch (_) { /* element may be gone */ }
      }
    };
  }, [ref, active, onEscape]);
}
